import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, Bell, Settings } from 'lucide-react';

const Privacy: React.FC = () => {
  const sections = [
    {
      title: 'Information We Collect',
      icon: <Database className="h-6 w-6" />,
      content: `We collect information that you provide directly to us, including:
        • Personal information (name, email address, phone number)
        • Educational background and career preferences
        • Assessment responses and career goals
        • Usage data and interaction with our services`
    },
    {
      title: 'How We Use Your Information',
      icon: <Eye className="h-6 w-6" />,
      content: `Your information is used to:
        • Provide personalized career recommendations
        • Match you with relevant mentors and opportunities
        • Improve our services and user experience
        • Send relevant updates and notifications`
    },
    {
      title: 'Data Security',
      icon: <Lock className="h-6 w-6" />,
      content: `We implement robust security measures to protect your data:
        • End-to-end encryption for sensitive information
        • Regular security audits and updates
        • Secure data storage and transmission
        • Access controls and authentication`
    },
    {
      title: 'User Rights',
      icon: <UserCheck className="h-6 w-6" />,
      content: `You have the right to:
        • Access your personal data
        • Request data correction or deletion
        • Opt-out of marketing communications
        • Export your data in a portable format`
    },
    {
      title: 'Notifications and Communications',
      icon: <Bell className="h-6 w-6" />,
      content: `We may send you notifications about:
        • Service updates and changes
        • Relevant career opportunities
        • Mentor matches and messages
        • Educational content and resources`
    },
    {
      title: 'Privacy Controls',
      icon: <Settings className="h-6 w-6" />,
      content: `You can control your privacy settings:
        • Manage notification preferences
        • Control data sharing options
        • Update visibility settings
        • Manage connected accounts`
    }
  ];

  return (
    <main className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <Shield className="h-16 w-16 text-primary-600 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-xl text-gray-600">
              Last updated: February 15, 2025
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-8">
                At Career Guidance+, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
                and protect your personal information when you use our services.
              </p>

              <div className="space-y-12">
                {sections.map((section, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border-b border-gray-200 pb-8 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-primary-100 rounded-lg text-primary-600 mr-3">
                        {section.icon}
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {section.title}
                      </h2>
                    </div>
                    <div className="pl-12">
                      <p className="text-gray-600 whitespace-pre-line">
                        {section.content}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-gray-600 text-sm">
                  For any privacy-related questions or concerns, please contact our Privacy Officer at{' '}
                  <a href="mailto:privacy@careerguidance.com" className="text-primary-600 hover:text-primary-700">
                    privacy@careerguidance.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default Privacy;